---
title: Extending TailwindCSS
---

I've been a fan of utility-based CSS since I built my first ever official design system based on [Buzzfeed Solid](https://www.buzzfeed.com/static-assets/solid-docs/index.html) back in 2016. Building this way has only gotten better with CSS variables and [TailwindCSS](https://tailwindcss.com/).

Most of my prototypes use TailwindCSS behind the scenes, no matter how different the end result looks.

If you don't know what Tailwinds is, take a peruse around their [documentation](https://tailwindcss.com/docs).

1. [Adding variables and classes to Tailwind](#adding-variables-and-classes-to-tailwind)
2. [Extending the shadcn theme](#extending-the-shadcn-theme)
3. [Working with LLMs](#working-with-llms)

## Adding variables and classes to Tailwind

Use `@theme` when you want a design token to be turned into utility classes, like `bg-red`, `text-red`, etc., and use `:root` for defining regular CSS variables that shouldn't have corresponding utility classes.

Some common things I extend all the time:

### Colours

Adding a color variable will make classes for backgrounds, texts, and border.

```css
@theme {
  --color-background: var(--background); /* bg-background, text-background, border-background */
  --color-foreground: var(--foreground); /* bg-foreground, text-foreground, border-foreground */
  --color-muted: var(--muted);           /* bg-muted, text-muted, border-muted */
  --color-code: var(--code);             /* bg-code, text-code, border-code */
}

:root {
  --background: oklch(1 0 0);
  --foreground: oklch(0.145 0 0);
}

.dark {
  --background: oklch(0.145 0.01 270);
  --foreground: oklch(0.985 0.005 270);
}
```

### Fonts

```css
@theme {
  --font-heading: "Inclusive Sans", system-ui, sans-serif; /* font-heading */
  --font-mono: "Andale Mono", ui-monospace;                /* font-mono */
}
```

### Breakpoints

```css
@theme {
  --breakpoint-sm: 640px;  /* sm:flex, sm:hidden, sm:p-4 */
  --breakpoint-md: 768px;  /* md:flex, md:hidden, md:p-4 */
  --breakpoint-lg: 1024px; /* lg:flex, lg:hidden, lg:p-4 */
  --breakpoint-xl: 1280px; /* xl:flex, xl:hidden, xl:p-4 */
}
```

### Animation

```css
@theme {
  --animate-fade-in: fade-in 0.3s ease-out;   /* animate-fade-in */
  --animate-slide-up: slide-up 0.3s ease-out; /* animate-slide-up */
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slide-up {
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
```

The full list of variables can be found in Tailwinds documentation: https://tailwindcss.com/docs/theme#theme-variable-namespaces

## Extending the shadcn theme

[shadcn](https://ui.shadcn.com/) is a collection of accessible, well-written components built on top of TailwindCSS and Radix or BaseCSS. It comes with a simple [theming system](https://ui.shadcn.com/docs/theming) making use of `colour` and `colour-foreground`. 

Using this system has made my life a lot easier when adding components, customizing interfaces, and using LLMs. I use this system in my [prototype generator](/tools/sandbox-new). 

The important thing to remember when extending shadcn and Tailwind at the same time, is to keep the `colour` and `colour-foreground` pattern, e.g:

A default shadcn theme might look something like: 

```css
:root {
  --radius: 0.625rem;
  --primary: oklch(0.205 0 0);
  --primary-foreground: oklch(0.985 0 0);
  --secondary: oklch(0.92 0 0);
  --secondary-foreground: oklch(0.205 0 0);
  --muted: oklch(0.97 0 0);
  --muted-foreground: oklch(0.556 0 0);
  --accent: oklch(0.145 0 0);
  --accent-foreground: oklch(0.985 0 0);
  --destructive: oklch(0.577 0.245 27.325);
  --border: oklch(0.922 0 0);
  --input: oklch(0.922 0 0);
  --ring: oklch(0.708 0 0);
}

@theme {
  --radius: 0.625rem;
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);
}
```

On this website, we created the highlighter-style headings by adding: 

```css
@theme {
  --color-highlight: var(--highlight);
  --color-highlight-foreground: var(--highlight-foreground);
}

:root {
  --highlight: oklch(0.9 0.2 85);
  --highlight-foreground: oklch(0 0 0);
}
```

This keeps the yellow and black headings no matter the theme. 

```jsx
<h1 className="bg-highlight text-highlight-foreground>
  Extending Tailwind
</h1>
```

## Working with LLMs

LLMs **love** Tailwinds due to their ubiquity and the depth of documentation available. However, I sometimes find Cursor can forget I've extended a theme and starts writing custom code. 

The following rule has done a pretty good job of keeping Tailwinds on track: 

```mdx
**Rules:**
1. NEVER use arbitrary values like `text-[12px]` when `text-caption` exists
2. NEVER use arbitrary spacing like `p-[16px]` when `p-m` exists
3. NEVER use arbitrary colors like `bg-[#fff]` when theme colors exist
4. Check `src/lib/plexwinds.css` and `src/lib/variables.css` for available design system tokens
5. Only use arbitrary values when no design system class exists for that specific valueN
```

And for shadcn: 
```mdx
### Component Priority:
1. **shadcn/ui components** - Check if a shadcn/ui component exists that meets your needs
   - Use `npx shadcn@latest add [component-name]` to add components
   - Components are located in `src/components/ui/`
   - These components are accessible, well-tested, and follow design system patterns

2. **Custom components** - Only create custom components when:
   - No shadcn/ui component exists for your use case
   - You need domain-specific functionality not covered by shadcn/ui
   - You're building a composite component that combines multiple shadcn/ui components
```

And that's pretty much it. Party on. 